/**
 * authorHandFrames — Hand Studio E3b (design: HAND_STUDIO_E3_JOINT_GRAFT.md §4,
 * RATIFIED 2026-07-14; incorporates the #37 thumb-frame correction). ONE
 * responsibility: give each E3a-placed joint an ANATOMICAL frame — the direction
 * the bone points (primary) and the dorsal curl reference (secondary) — so that
 * the ontology's swing-twist limits, once E3c grafts the bone, apply in the RIGHT
 * basis. This is the piece v1/v2 never realized (§2): constraints failed to map
 * because no per-joint anatomical frame existed on an arbitrary rig. E3 grafts the
 * bones, so E3b AUTHORS the frame from evidence it holds.
 *
 * The frame, per joint, in VOXEL-INDEX space (the space E1/E3a live in — the
 * voxel→world transform + the bone-local `JointFrame` conversion are E3c's):
 *   - `primary`   = the digit-chain segment direction (this joint → its child).
 *   - `secondary` = the PALM NORMAL, the dorsal "nail-up" reference, SHARED by
 *     every digit including the thumb (the #37 fix: the thumb differs in its
 *     PRIMARY — the opposition diagonal falls out of its own branch — NOT its
 *     secondary; the ontology's `sec = UP` is the frame its `THUMB_MCP` limits are
 *     authored against). `secondary` is Gram-Schmidt-orthogonalized against
 *     `primary` and renormalized (the `jointFrameSchema` gate wants unit + ⊥).
 *
 * The palm normal is the smallest-variance PCA axis of the occupied hand volume
 * (the thin axis of a flat hand — §10a probe: well-conditioned, width/thickness
 * ~2.4 on the goblin). A hand too CHUNKY for that measurement (the purple furry
 * monster's paw: conditioning 1.23, nearly as thick as wide, 2026-07-27) does
 * not refuse outright: with ≥2 placed fingers the palm plane is CONSTRUCTED
 * from the digits instead — knuckle-row axis × mean distal direction — which
 * spans the palm whatever the flesh thickness, and is marked
 * `palm-normal-from-digits` (provenance, §11). Only a hand where BOTH sources
 * fail refuses. The normal's AXIS is measured either way; its SIGN (dorsal vs
 * palmar) is genuinely underdetermined from hand shape alone, so it is oriented
 * toward a `dorsalHint` (default the ontology's palms-down `+Y` — the SAME
 * declared-convention assumption the reorientation ladder's declared-frame tier
 * already makes). This keeps BOTH hands dorsal-consistent (the ontology's
 * `sec = UP` for Left and Right), where a chirality-based convention would mirror
 * them. A rig not resting in that convention needs a real up/facing cue or a user
 * confirm — surfaced as a correctable marker (§11), never silently trusted.
 *
 * Fail-loud (LAWS): a degenerate palm (conditioning below the floor) or a joint
 * whose primary is parallel to the palm normal (no frame formable) throws
 * {@link FrameAuthoringError}. A large orthogonalization correction (palm and
 * branch geometry disagree) is a named marker, not a throw — a thumb's diagonal
 * primary legitimately needs more correction than a flat finger.
 *
 * Depends on: hands/place-digits (E3a), hands/voxel-grid, hands/separation-profile
 * (marker type). Depended on by: E3c graft (later), kernel index.
 */
import { KernelError } from '../ir/errors.js';
import type { VoxelGrid, VoxelPoint } from './voxel-grid.js';
import type { DigitJoint } from './digit-anatomy.js';
import type { EvidenceMarker } from './separation-profile.js';
import type { DigitName, PlacedDigit, PlacedHand } from './place-digits.js';

/** A cannot-author-frame condition — fail-loud (ADR-010 §7). */
export class FrameAuthoringError extends KernelError {
  constructor(readonly issues: readonly string[]) {
    super(`Cannot author hand frames (${issues.length} issue(s)):\n` + issues.map((i) => `  - ${i}`).join('\n'));
  }
}

/** One grafted bone's authored anatomical frame, voxel-index space. */
export interface JointFrameGeom {
  /** BBSA ontology id this frame is for (from E3a `boneIds`; NEVER a `…4` tip). */
  readonly ontologyId: string;
  /**
   * Which joint this frame is for. Only the three ARTICULATING joints get one —
   * tips are non-articulating. The shared union, so a thumb's `cmc/mcp/ip`
   * cannot be silently relabelled as a finger's `mcp/pip/dip` (ADR-023 §6).
   */
  readonly joint: DigitJoint;
  /** Unit direction the bone points (joint → child). */
  readonly primary: VoxelPoint;
  /** Unit direction ⊥ primary — the dorsal curl reference (shared palm normal). */
  readonly secondary: VoxelPoint;
  /** Degrees the palm normal was rotated to become ⊥ primary (evidence, §4). */
  readonly orthogonalizationDeg: number;
}

export interface DigitFrames {
  readonly name: DigitName;
  readonly frames: readonly JointFrameGeom[];
}

export interface HandFrames {
  readonly side: 'left' | 'right';
  /** Unit dorsal palm normal (voxel space); sign is a geometric convention. */
  readonly palmNormal: VoxelPoint;
  /** width/thickness σ ratio of the palm plane (higher = flatter/better). */
  readonly palmConditioning: number;
  readonly digits: readonly DigitFrames[];
  readonly markers: readonly EvidenceMarker[];
}

export interface AuthorFramesOptions {
  /** Reject a palm whose width/thickness conditioning is below this (default 1.5). */
  readonly palmConditioningFloor?: number;
  /** Flag (marker) a joint needing more than this orthogonalization (default 30°). */
  readonly orthogonalizationFlagDeg?: number;
  /**
   * The rig's dorsal ("nail-up") direction the palm normal is oriented toward —
   * default the ontology's palms-down `+Y`. Pass the rig's declared up-axis when
   * it is known; a rig not resting palms-down needs this or a user confirm.
   */
  readonly dorsalHint?: VoxelPoint;
  /**
   * THE THUMB'S OPPOSITION (2026-08-15, field — Sergio, Kara: "the thumb twist
   * leans to the wrong side"): how far the thumb's dorsal reference is rotated
   * from the fingers' palm normal toward the RADIAL side of the hand, in
   * degrees. A thumb is not a finger here either: its curl plane is rotated
   * toward radial — the thumbnail faces out along the palm's edge, not up with
   * the fingers — so giving it the finger frame lands a source flexion partly
   * on its abduction axis: it curls, leaning the wrong way. Anatomical
   * opposition is ~90°; a flat generated bind may want less. Radial is measured
   * from the placement (finger knuckle row's mean → thumb MCP, in the palm
   * plane), so it mirrors per side by construction. Default 90. A §11 knob with
   * provenance; 0 restores the finger frame exactly.
   */
  readonly thumbOppositionDeg?: number;
}

// ---- self-contained VoxelPoint math (E3b is pure geometry) --------------------
const ZERO: VoxelPoint = { x: 0, y: 0, z: 0 };
const sub = (a: VoxelPoint, b: VoxelPoint): VoxelPoint => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const add = (a: VoxelPoint, b: VoxelPoint): VoxelPoint => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });
const scale = (a: VoxelPoint, s: number): VoxelPoint => ({ x: a.x * s, y: a.y * s, z: a.z * s });
const dot = (a: VoxelPoint, b: VoxelPoint): number => a.x * b.x + a.y * b.y + a.z * b.z;
const len = (a: VoxelPoint): number => Math.hypot(a.x, a.y, a.z);
const cross = (a: VoxelPoint, b: VoxelPoint): VoxelPoint => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});
function normalize(a: VoxelPoint): VoxelPoint {
  const n = len(a);
  if (!(n > 0)) return ZERO;
  return scale(a, 1 / n);
}

/** All three covariance eigenpairs (power iteration + deflation), sorted desc. */
function eigen(points: readonly VoxelPoint[]): Array<{ value: number; vector: VoxelPoint }> {
  const mean = scale(points.reduce(add, ZERO), 1 / points.length);
  let m = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  for (const p of points) {
    const d = sub(p, mean);
    const v = [d.x, d.y, d.z];
    for (let r = 0; r < 3; r += 1) for (let c = 0; c < 3; c += 1) m[r]![c]! += v[r]! * v[c]!;
  }
  m = m.map((r) => r.map((x) => x / points.length));
  const mul = (mm: number[][], v: VoxelPoint): VoxelPoint => ({
    x: mm[0]![0]! * v.x + mm[0]![1]! * v.y + mm[0]![2]! * v.z,
    y: mm[1]![0]! * v.x + mm[1]![1]! * v.y + mm[1]![2]! * v.z,
    z: mm[2]![0]! * v.x + mm[2]![1]! * v.y + mm[2]![2]! * v.z,
  });
  const out: Array<{ value: number; vector: VoxelPoint }> = [];
  const seeds: VoxelPoint[] = [
    { x: 1, y: 0.31, z: 0.11 },
    { x: 0.13, y: 1, z: 0.27 },
    { x: 0.17, y: 0.23, z: 1 },
  ];
  for (let k = 0; k < 3; k += 1) {
    let v = normalize(seeds[k]!);
    for (let i = 0; i < 250; i += 1) {
      const w = mul(m, v);
      if (len(w) < 1e-14) break;
      v = normalize(w);
    }
    const value = dot(mul(m, v), v);
    out.push({ value, vector: v });
    for (let r = 0; r < 3; r += 1) for (let c = 0; c < 3; c += 1) m[r]![c]! -= value * (r === 0 ? v.x : r === 1 ? v.y : v.z) * (c === 0 ? v.x : c === 1 ? v.y : v.z);
  }
  out.sort((a, b) => b.value - a.value);
  return out;
}

/** Decode occupied linear indices → voxel coordinates (the E1 `x + nx*(y + ny*z)` layout). */
function occupiedCoords(grid: VoxelGrid): VoxelPoint[] {
  const [nx, ny] = grid.dims;
  const pts: VoxelPoint[] = [];
  for (const i of grid.occupied) pts.push({ x: i % nx, y: Math.floor(i / nx) % ny, z: Math.floor(i / (nx * ny)) });
  return pts;
}

/**
 * Gram-Schmidt unit `ref` against unit `primary` → unit ⊥ component + the
 * deviation-from-perpendicular of `ref` (deg). If `ref` is already ⊥ `primary`
 * the correction is 0°; the thumb's diagonal primary legitimately yields more.
 */
function orthogonalize(ref: VoxelPoint, primary: VoxelPoint): { sec: VoxelPoint; deg: number } {
  const d = dot(ref, primary); // cos(angle between ref and primary), ref & primary unit
  const sec = normalize(sub(ref, scale(primary, d)));
  const thetaDeg = (Math.acos(Math.min(1, Math.max(-1, d))) * 180) / Math.PI;
  return { sec, deg: Number(Math.abs(90 - thetaDeg).toFixed(2)) };
}

/** The three ARTICULATING joints (in E3a placement order); the …4 tip gets no frame. */
/**
 * How many joints articulate — the …4 tip is a leaf marker and gets no frame.
 * WHAT they are called is no longer decided here: it comes from the placed
 * joint itself (ADR-023 §6, Codex P2 #96). The previous version indexed a
 * `['mcp','pip','dip']` tuple by array POSITION, so a thumb migrated to
 * `cmc/mcp/ip` would have kept compiling while silently emitting finger names —
 * the enforcement this ADR claimed, not actually connected to anything.
 */
const ARTICULATING_COUNT = 3;
/** Anatomical thumb opposition — how far the thumb's dorsal is rotated toward radial (see `thumbOppositionDeg`). */
export const THUMB_OPPOSITION_DEG_DEFAULT = 90;

/**
 * Author each placed joint's anatomical frame. See the module header for the full
 * contract. Returns {@link HandFrames}; throws {@link FrameAuthoringError} on a
 * degenerate palm or an unformable frame.
 */
export function authorHandFrames(placed: PlacedHand, grid: VoxelGrid, options: AuthorFramesOptions = {}): HandFrames {
  const conditioningFloor = options.palmConditioningFloor ?? 1.5;
  const flagDeg = options.orthogonalizationFlagDeg ?? 30;
  const markers: EvidenceMarker[] = [];

  if (placed.digits.length === 0) {
    throw new FrameAuthoringError(['no placed digits — resolve the thumb pin (E3a returned unresolved)']);
  }

  // --- palm normal: smallest-variance axis of the occupied hand volume ---
  const coords = occupiedCoords(grid);
  if (coords.length < 3) throw new FrameAuthoringError([`too few occupied voxels (${coords.length}) for a palm plane`]);
  const eigs = eigen(coords);
  const sigma = eigs.map((e) => Math.sqrt(Math.max(0, e.value)));
  // Line/point-degenerate: with the SECOND spread near zero there is no plane at
  // all (any plane through the line fits equally) — reject before conditioning,
  // which would otherwise read a spurious Infinity (Codex #39 P2).
  if (!(sigma[1]! > 1e-9) || sigma[1]! < 1e-6 * (sigma[0]! || 1)) {
    throw new FrameAuthoringError([`degenerate palm: rank-deficient volume (σ=${sigma.map((s) => s.toFixed(3)).join(',')}) — no plane`]);
  }
  // The palm normal is the NULLSPACE of the two dominant axes (robust), not the
  // deflated smallest eigenvector (arbitrary when the thickness variance ≈ 0, so a
  // perfectly flat slab would otherwise get a garbage normal) — Codex #39 P2.
  const palmNormalRaw = cross(eigs[0]!.vector, eigs[1]!.vector);
  if (!(len(palmNormalRaw) > 1e-9)) {
    throw new FrameAuthoringError(['degenerate palm: dominant axes are parallel — no plane normal']);
  }
  const conditioning = sigma[2]! > 1e-9 ? sigma[1]! / sigma[2]! : Infinity;
  let palmNormal: VoxelPoint;
  if (conditioning >= conditioningFloor) {
    palmNormal = normalize(palmNormalRaw);
  } else {
    // A paw too chunky for the volume to know its own back (module header).
    // The DIGITS know: the knuckle row crossed with the fingers' shared distal
    // direction spans the palm plane at any flesh thickness. Constructed, and
    // said so — never a silent guess, and refusal only when this too fails.
    const fingers = placed.digits.filter((d) => d.name !== 'Thumb' && d.joints.length === 4);
    if (fingers.length < 2) {
      throw new FrameAuthoringError([
        `degenerate palm plane: width/thickness conditioning ${conditioning.toFixed(2)} < floor ` +
          `${conditioningFloor}, and only ${fingers.length} placed finger(s) — the knuckle-row ` +
          `fallback needs at least 2 (assign more digits, or re-voxelize)`,
      ]);
    }
    const rowAxis = eigen(fingers.map((d) => d.joints[0]!.position))[0]!.vector;
    const distal = normalize(
      fingers.reduce((acc, d) => add(acc, normalize(sub(d.joints[3]!.position, d.joints[0]!.position))), ZERO),
    );
    const constructed = cross(rowAxis, distal);
    if (!(len(constructed) > 1e-6)) {
      throw new FrameAuthoringError([
        `degenerate palm plane: width/thickness conditioning ${conditioning.toFixed(2)} < floor ` +
          `${conditioningFloor}, and the knuckle row is parallel to the finger direction — ` +
          `no palm plane from the digits either`,
      ]);
    }
    palmNormal = normalize(constructed);
    markers.push({
      code: 'palm-normal-from-digits',
      detail: {
        conditioning: Number(conditioning.toFixed(3)),
        floor: conditioningFloor,
        fingers: fingers.length,
        note: 'volume too chunky for a PCA palm plane — normal constructed from knuckle row × distal direction',
      },
    });
  }

  // --- dorsal sign: orient the (sign-ambiguous) axis toward the dorsal hint ---
  // Default +Y = the ontology's palms-down convention; keeps L/R consistent
  // (both dorsal), where a chirality rule would mirror them.
  const dorsalHint = normalize(options.dorsalHint ?? { x: 0, y: 1, z: 0 });
  const alignedToHint = len(dorsalHint) > 1e-9 && dot(palmNormal, dorsalHint) >= 0;
  if (len(dorsalHint) > 1e-9 && dot(palmNormal, dorsalHint) < 0) palmNormal = scale(palmNormal, -1);
  markers.push({
    code: 'palm-normal-sign',
    detail: { convention: 'dorsal-hint', hintAgreementAtMeasure: alignedToHint, note: 'true dorsal vs palmar may need a mesh-facing cue / user confirm (§11)' },
  });

  // --- frames for the THREE ARTICULATING joints only (mcp/pip/dip). Tips (…4) are
  // non-articulating — the ontology rejects a jointFrame for them (Codex #39 P2);
  // the tip position is used ONLY as the DIP's forward child. ---
  // ── THE THUMB'S DORSAL REFERENCE: the palm normal, rotated toward radial ──
  const oppositionDeg = options.thumbOppositionDeg ?? THUMB_OPPOSITION_DEG_DEFAULT;
  if (!Number.isFinite(oppositionDeg)) throw new FrameAuthoringError([`thumbOppositionDeg ${oppositionDeg} is not a number`]);
  let thumbRef: VoxelPoint = palmNormal;
  {
    const thumb = placed.digits.find((d) => d.name === 'Thumb');
    const fingers = placed.digits.filter((d) => d.name !== 'Thumb' && d.joints.length >= 2);
    if (thumb !== undefined && thumb.joints.length >= 2 && oppositionDeg !== 0) {
      if (fingers.length === 0) {
        // A THUMB-ONLY HAND (Codex P2, #97 round seven): the radial direction
        // is measured against the finger knuckle row, and this hand has none —
        // inventing one (wrist→thumb, say) would author the most contested
        // frame on the hand from geometry nobody measured. The declaration is
        // recorded in the Document either way, so its non-application must be
        // SAID, in the same voice as the on-normal case below — never skipped
        // in silence.
        markers.push({
          code: 'thumb-frame-opposition-unmeasured',
          detail: { note: `no non-thumb fingers — no knuckle row to take a radial from; finger frame kept (declared ${oppositionDeg}° unapplied)` },
        });
      } else {
        const rowMean = scale(
          fingers.reduce((acc, d) => add(acc, d.joints[0]!.position), ZERO),
          1 / fingers.length,
        );
        // The thumb's MCP (its second node) against the finger knuckle row — the
        // radial direction, taken IN the palm plane so it is ⊥ the palm normal.
        const toThumb = sub(thumb.joints[1]!.position, rowMean);
        const radialRaw = sub(toThumb, scale(palmNormal, dot(toThumb, palmNormal)));
        if (len(radialRaw) > 1e-9) {
          const radial = normalize(radialRaw);
          const t = (oppositionDeg * Math.PI) / 180;
          thumbRef = normalize(add(scale(palmNormal, Math.cos(t)), scale(radial, Math.sin(t))));
          markers.push({ code: 'thumb-frame-opposition', detail: { deg: oppositionDeg } });
        } else {
          markers.push({ code: 'thumb-frame-opposition-unmeasured', detail: { note: 'thumb MCP sits on the palm normal through the row — finger frame kept' } });
        }
      }
    }
  }

  const digits: DigitFrames[] = placed.digits.map((digit: PlacedDigit) => {
    const frames: JointFrameGeom[] = [];
    const ref = digit.name === 'Thumb' ? thumbRef : palmNormal;
    for (let i = 0; i < ARTICULATING_COUNT; i += 1) {
      const child = digit.joints[i + 1]; // pip, dip, then the tip (for dip's primary)
      if (child === undefined) break; // a digit short of MCP/PIP/DIP+tip — skip the rest
      const primaryRaw = sub(child.position, digit.joints[i]!.position);
      if (!(len(primaryRaw) > 0)) {
        throw new FrameAuthoringError([`${digit.boneIds[i]}: zero-length segment — coincident joints, no primary direction`]);
      }
      const primary = normalize(primaryRaw);
      if (Math.abs(dot(primary, ref)) > 0.999) {
        throw new FrameAuthoringError([`${digit.boneIds[i]}: primary is parallel to the dorsal reference — no frame formable`]);
      }
      const { sec, deg } = orthogonalize(ref, primary);
      if (deg > flagDeg) markers.push({ code: 'frame-orthogonalization-large', detail: { bone: digit.boneIds[i]!, deg } });
      frames.push({
        ontologyId: digit.boneIds[i]!,
        joint: digit.joints[i]!.joint,
        primary,
        secondary: sec,
        orthogonalizationDeg: deg,
      });
    }
    return { name: digit.name, frames };
  });

  return {
    side: placed.side,
    palmNormal,
    palmConditioning: Number(Math.min(conditioning, 9999).toFixed(3)),
    digits,
    markers,
  };
}
